'use client';

import { Chip } from '@startuptools/ui/joy/Chip';
import { ColorPaletteProp } from '@mui/joy/styles';
import {
  GqlOptionOverviewQuery,
  OptionOverviewDocument,
  useOptionOverviewSuspenseQuery,
  useWppExplainVestingScheduleMutation,
} from '../option-overview.graphql';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Trans, useLingui } from '@lingui/react';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Card } from '@startuptools/ui/joy/Card';
import { Column, ItemList } from '@startuptools/ui/ItemList';
import StopIcon from '@mui/icons-material/Stop';
import React from 'react';
import { EndVesting } from '../../common/components/steps/or-subscribers/EndVesting';
import { useCurrentUserSuspenseQuery, useWppUpdateMutation } from '../../../../graphql/react-operations';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { Modal } from '@startuptools/ui/joy/Modal';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import CommentIcon from '@mui/icons-material/Comment';
import { CommentDialog } from '../../../common/components/CommentDialog';
import { BigNumber } from 'bignumber.js';
import {
  GqlSortOrder,
  GqlWarrantProgramKind,
  GqlWppSort,
  GqlVestingStatus,
} from '../../../../graphql/base-types.graphql';
import { VestingChart } from '@startuptools/business-ui/vesting';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { apiClient } from '../../../../common/http';
import { CardContent } from '@startuptools/ui/joy/CardContent';
import { CommentCell } from '@startuptools/ui/CommentCell';
import { Box } from '@startuptools/ui/joy/Box';
import { Repurchase } from '../../common/components/steps/or-subscribers/Repurchase';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Link } from '@startuptools/ui/joy/Link';
import { DownloadFile } from '../../../../common/DownloadFile';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton } from '@startuptools/ui/joy/IconButton';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Switch } from '@startuptools/ui/joy/Switch';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import PageviewIcon from '@mui/icons-material/Pageview';
import { PaymentStatus } from '@startuptools/common/participants';
import { DocumentDialogContent } from '../../../../components/document-dialog/DocumentDialog';
import { wpEditLink } from '../../../../components/Links';
import { useNavigate } from '../../../../components/Navigate';
import { Link as RouterLink } from 'react-router-dom';

type Wpp = GqlOptionOverviewQuery['company']['warrantParticipants']['models'][0];

interface OptionsListProps {
  data?: Pick<GqlOptionOverviewQuery['company'], 'warrantParticipants'>;
  companyId: string;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  sort: GqlWppSort;
  setSort: (sort: GqlWppSort) => void;
  showSplitAdjusted: boolean;
  setShowSplitAdjusted: (showSplitAdjusted: boolean) => void;
}

export const OptionsList = ({
  data,
  companyId,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  sort,
  setSort,
  showSplitAdjusted,
  setShowSplitAdjusted,
}: OptionsListProps) => {
  const [excelUrl, setExcelUrl] = React.useState<string | null>(null);
  const { i18n } = useLingui();
  const { link } = useNavigate();
  const {
    data: { currentUser },
  } = useCurrentUserSuspenseQuery();
  const [pending, startTransition] = React.useTransition();

  const [explainVestingSchedule, { loading: loadingExplanation }] = useWppExplainVestingScheduleMutation();

  const [showDialog, setShowDialog] = React.useState<{
    type: 'comment' | 'endVesting' | 'vestingSchedule' | 'repurchase' | 'document';
    participant: Wpp;
  } | null>(null);

  const [updateWpp, { loading }] = useWppUpdateMutation({ refetchQueries: [OptionOverviewDocument] });

  const hasDoneSplit = React.useMemo(() => {
    return data?.warrantParticipants.models.some(item => !new BigNumber(item.splitFactor ?? '1').isEqualTo('1'));
  }, [data?.warrantParticipants.models]);

  const columns: Column<Wpp>[] = [
    {
      header: <Trans id="Name" />,
      sortKey: 'identity.name',
      render: item => <Identity identity={item.identity} />,
      width: '200px',
    },
    {
      header: <Trans id="Program" />,
      render: item => (
        <>
          <Link
            component={RouterLink}
            to={link(wpEditLink(item.optionsRegistration.warrantProgram))}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {item.optionsRegistration.warrantProgram.name}
          </Link>
          <Typography fontSize="xs" color="neutral">
            {item.optionsRegistration.warrantProgram.warrantKind === GqlWarrantProgramKind.Employee ? (
              <Trans id="Employee Options" />
            ) : (
              <Trans id="Warrant Programs" />
            )}
          </Typography>
        </>
      ),
    },
    {
      header: <Trans id="Options" />,
      sortKey: 'quantity',
      render: item => (
        <>
          <Typography sx={new BigNumber(item.quantityRepurchased).gt(0) ? { textDecoration: 'line-through' } : {}}>
            <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
              {item.quantity}
            </NumberFormat>
          </Typography>{' '}
          {new BigNumber(item.quantityRepurchased).gt(0) && (
            <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
              {new BigNumber(item.quantity ?? 0).minus(item.quantityRepurchased).toString()}
            </NumberFormat>
          )}
        </>
      ),
    },
    {
      header: <Trans id="Vested" />,
      render: item => (
        <Stack alignItems="center" gap={2} direction="row" justifyContent="space-between">
          <NumberFormat locale={i18n.locale} maximumFractionDigits={0} minimumFractionDigits={0} roundingMode="floor">
            {item.vestedQuantity}
          </NumberFormat>
          <IconButton
            size="sm"
            variant="outlined"
            onClick={() => {
              setShowDialog({ type: 'vestingSchedule', participant: item });
            }}
          >
            <InfoIcon />
          </IconButton>
        </Stack>
      ),
    },
    {
      header: <Trans id="Invalidated" />,
      render: item => (
        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
          {item.quantityInvalidated}
        </NumberFormat>
      ),
      show: data?.warrantParticipants.models.some(item => Boolean(item.quantityInvalidated)),
    },
    {
      header: <Trans id="Vesting Status" />,
      render: item => {
        const color: Record<GqlVestingStatus, ColorPaletteProp> = {
          [GqlVestingStatus.Pending]: 'neutral',
          [GqlVestingStatus.Ongoing]: 'primary',
          [GqlVestingStatus.Completed]: 'success',
          [GqlVestingStatus.Canceled]: 'warning',
        };

        const label: Record<GqlVestingStatus, React.ReactNode> = {
          [GqlVestingStatus.Pending]: <Trans id="Pending" />,
          [GqlVestingStatus.Ongoing]: <Trans id="Ongoing" />,
          [GqlVestingStatus.Completed]: <Trans id="Completed" />,
          [GqlVestingStatus.Canceled]: <Trans id="Canceled" />,
        };

        return <Chip color={color[item.vestingStatus]}>{label[item.vestingStatus]}</Chip>;
      },
    },
    {
      header: <Trans id="Documents" />,
      render: item => {
        if (item.latestAgreementDoc) {
          return (
            <IconButton
              size="sm"
              variant="outlined"
              onClick={() => {
                setShowDialog({ type: 'document', participant: item });
              }}
            >
              <PageviewIcon />
            </IconButton>
          );
        }
        return (
          <Chip color="neutral">
            <Trans id="No documents" />
          </Chip>
        );
      },
    },
    {
      header: <Trans id="Payed for" />,
      render: item => {
        if (item.optionsRegistration.warrantProgram.warrantKind === GqlWarrantProgramKind.Employee) {
          return (
            <Chip color="neutral">
              <Trans id="Not applicable" />
            </Chip>
          );
        }
        const color: Record<PaymentStatus, ColorPaletteProp> = {
          [PaymentStatus.NotPaid]: 'danger',
          [PaymentStatus.Paid]: 'success',
          [PaymentStatus.WaitingForPayment]: 'neutral',
        };

        const text: Record<PaymentStatus, React.ReactNode> = {
          [PaymentStatus.NotPaid]: <Trans id="Not paid" />,
          [PaymentStatus.Paid]: <Trans id="Paid" />,
          [PaymentStatus.WaitingForPayment]: <Trans id="Waiting for payment" />,
        };

        return (
          <Chip color={color[item.paymentStatus as unknown as PaymentStatus]}>
            {text[item.paymentStatus as unknown as PaymentStatus]}
          </Chip>
        );
      },
    },
  ];

  const actions = (item: Wpp) => {
    return [
      {
        icon: <StopIcon />,
        label: <Trans id="End Vesting" />,
        onClick: () => {
          setShowDialog({ type: 'endVesting', participant: item });
        },
      },
      {
        icon: <CommentIcon />,
        label: <Trans id="Comment" />,
        onClick: () => {
          setShowDialog({ type: 'comment', participant: item });
        },
      },
      // {
      //   icon: <BackspaceIcon />,
      //   label: <Trans id="Repurchase" />,
      //   onClick: () => {
      //     setShowDialog({ type: 'repurchase', participant: item });
      //   },
      // },
    ];
  };

  return (
    <>
      {excelUrl && (
        <DownloadFile
          url={excelUrl}
          fileName={`option-overview.xlsx`}
          onClose={() => {
            window.URL.revokeObjectURL(excelUrl);
            setExcelUrl(null);
          }}
        />
      )}

      <ItemList
        pending={pending}
        beforeTableSection={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {hasDoneSplit && (
              <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                <Typography level="body-sm">
                  <Trans id="Show split-adjusted quantities" />
                </Typography>
                <Switch
                  checked={showSplitAdjusted}
                  onChange={event => {
                    startTransition(() => {
                      setShowSplitAdjusted(event.target.checked);
                    });
                  }}
                  size="sm"
                />
              </FormControl>
            )}
          </Box>
        }
        skeletonCount={!data ? 5 : undefined}
        globalActions={
          (data?.warrantParticipants.totalCount || 0) > 0
            ? [
                {
                  icon: <FileDownloadIcon />,
                  label: <Trans id="Export" />,
                  onClick: async () => {
                    const url = await apiClient.getOptionProgramExcelExportUrl(companyId);
                    setExcelUrl(url);
                  },
                },
              ]
            : undefined
        }
        pagination={{
          totalCount: data?.warrantParticipants.totalCount || 0,
          page,
          rowsPerPage,
          setPage: value => setPage(value),
          setRowsPerPage: value => setRowsPerPage(value),
        }}
        actions={actions}
        sort={sort}
        setSort={({ field, direction }) => setSort({ field, direction: direction as GqlSortOrder })}
        items={data?.warrantParticipants.models ?? []}
        columns={columns}
        defaultView="table"
        extraRow={{
          render: item => {
            return item.comment ? (
              <tr>
                <td valign="top">
                  <Box>
                    <Typography fontWeight={600}>
                      <Trans id="Comment:" />
                    </Typography>
                  </Box>
                </td>
                <CommentCell colSpan={columns.length - 1} comment={item.comment} />
                <td></td>
              </tr>
            ) : undefined;
          },
        }}
        emptyState={
          data?.warrantParticipants.totalCount === 0 && (
            <Card
              size="lg"
              variant="plain"
              orientation="horizontal"
              sx={{
                textAlign: 'center',
                width: '100%',
                overflow: 'auto',
              }}
            >
              <CardContent sx={{ gap: 1.5, minWidth: 200, m: 2 }}>
                <Typography level="title-lg">
                  <Trans id="No options subscribers found" />
                </Typography>
                <Typography fontSize="sm" sx={{ mt: 0.5 }}>
                  <Trans id="After you've issued your first option program this view will show all outstanding options across the company." />
                </Typography>
              </CardContent>
            </Card>
          )
        }
      />

      {showDialog && (
        <ErrorBoundary showModal>
          <Modal open onClose={() => setShowDialog(null)}>
            <ModalDialog
              sx={{
                maxHeight: 'calc(100vh - 64px)',
                my: 4,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
              }}
            >
              <React.Suspense>
                {showDialog.type === 'document' && (
                  <DocumentDialogContent document={showDialog.participant.latestAgreementDoc!} />
                )}
                {showDialog.type === 'comment' && (
                  <CommentDialog
                    onClose={() => setShowDialog(null)}
                    comment={showDialog?.participant.comment}
                    onSave={async comment => {
                      await updateWpp({
                        variables: { companyId, input: { comment, id: showDialog!.participant.id } },
                      });
                    }}
                    loading={loading}
                  />
                )}
                {showDialog.type === 'repurchase' && (
                  <Repurchase
                    closeDialog={() => setShowDialog(null)}
                    onSubmit={async formData => {
                      await updateWpp({
                        variables: {
                          companyId,
                          input: { quantityRepurchased: formData.quantityRepurchased, id: showDialog.participant.id },
                        },
                      });
                    }}
                    participant={showDialog.participant}
                    loading={loading}
                  />
                )}
                {showDialog.type === 'endVesting' && (
                  <EndVesting
                    closeDialog={() => setShowDialog(null)}
                    onSubmit={async formData => {
                      await updateWpp({
                        variables: {
                          companyId,
                          input: {
                            id: showDialog.participant.id,
                            vestingEndDate:
                              formData.vestingEndDate?.toUTC(0, { keepLocalTime: true }).toISODate() ?? null,
                            quantityInvalidated: formData.quantityInvalidated ?? null,
                          },
                        },
                      });
                    }}
                    participant={showDialog.participant}
                    wpp={showDialog.participant.optionsRegistration.warrantProgram}
                    loading={loading}
                  />
                )}
                {showDialog.type === 'vestingSchedule' && (
                  <VestingChart
                    wpp={showDialog.participant}
                    vestingOccasions={showDialog.participant.vestingOccasions}
                    explanationProps={
                      currentUser.admin
                        ? {
                            loading: loadingExplanation,
                            fetchExplanation: async () => {
                              const res = await explainVestingSchedule({
                                variables: { id: showDialog.participant.id },
                              });
                              const { data } = res;
                              if (data) {
                                setShowDialog(state => ({
                                  type: 'vestingSchedule',
                                  participant: {
                                    ...state!.participant,
                                    vestingExplanationPrimaryLang:
                                      data.wppExplainVestingSchedule.vestingExplanationPrimaryLang,
                                    vestingExplanationSecondaryLang:
                                      data.wppExplainVestingSchedule.vestingExplanationSecondaryLang,
                                  },
                                }));
                              }
                            },
                          }
                        : undefined
                    }
                  />
                )}
              </React.Suspense>
            </ModalDialog>
          </Modal>
        </ErrorBoundary>
      )}
    </>
  );
};

export const OptionsListSuspense = () => {
  const companyId = useCompanyId();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [showSplitAdjusted, setShowSplitAdjusted] = React.useState(false);
  const [sort, setSort] = React.useState<GqlWppSort>({ field: 'identity.name', direction: GqlSortOrder.Desc });
  const { data } = useOptionOverviewSuspenseQuery({
    variables: {
      companyId,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      sort,
      showSplitAdjusted,
    },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <OptionsList
      data={data.company}
      companyId={companyId}
      page={page}
      setPage={setPage}
      rowsPerPage={rowsPerPage}
      setRowsPerPage={setRowsPerPage}
      sort={sort}
      setSort={setSort}
      showSplitAdjusted={showSplitAdjusted}
      setShowSplitAdjusted={setShowSplitAdjusted}
    />
  );
};
